import { DecisionAnswer } from "@/types";

const formatPercent = (probability: number) => `${Math.round(probability * 100)}%`;

const formatAnswer = (answer: DecisionAnswer) => {
  if (answer.type === "refusal") {
    return "Refused";
  }
  if (answer.type === "binary" && typeof answer.value === "number") {
    return answer.value >= 0.5 ? "Yes" : "No";
  }
  if (answer.type === "score" && typeof answer.value === "number") {
    return answer.value.toFixed(2);
  }
  return String(answer.value);
};

const getSummary = (answer: DecisionAnswer) => {
  if (answer.type === "binary" && typeof answer.value === "number") {
    return `${formatPercent(answer.value)} yes`;
  }
  if (answer.confidence !== null) {
    return `${formatPercent(answer.confidence)} confidence`;
  }
  return null;
};

const ProbabilityBar = ({ label, probability }: { label: string; probability: number }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-28 shrink-0 truncate text-gray-600" title={label}>
      {label}
    </span>
    <div className="h-1.5 flex-1 rounded-full bg-gray-200">
      <div
        className="h-1.5 rounded-full bg-blue-500"
        style={{ width: formatPercent(probability) }}
      />
    </div>
    <span className="w-9 shrink-0 text-right tabular-nums text-gray-500">
      {formatPercent(probability)}
    </span>
  </div>
);

const DecisionAnswerRow = ({ answer }: { answer: DecisionAnswer }) => {
  const summary = getSummary(answer);
  const showProbabilities = answer.type === "choice" || answer.type === "score";

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-sm text-gray-600">{answer.name}</span>
        <span className="text-sm font-semibold text-gray-900">{formatAnswer(answer)}</span>
        {summary && <span className="ml-auto text-xs text-gray-500">{summary}</span>}
      </div>
      {showProbabilities && (
        <div className="flex flex-col gap-1">
          {Object.entries(answer.probabilities).map(([label, probability]) => (
            <ProbabilityBar key={label} label={label} probability={probability} />
          ))}
        </div>
      )}
    </div>
  );
};

export const DecisionAnswersResponse = ({ answers }: { answers: DecisionAnswer[] }) => (
  <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
    {answers.map(answer => (
      <DecisionAnswerRow key={answer.name} answer={answer} />
    ))}
  </div>
);
